import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, ShoppingCart, Plus, Minus, FileText } from "lucide-react";
import { fmtQty } from "@/lib/pack";

/**
 * AddToDraftDialog — "add-on-the-run" flow.
 *
 * Staff notice an item running low while walking the shelves. Rather
 * than a sticky-note, they open this dialog from the item card and:
 *   - if a Draft PO already exists for the item's supplier → merged into it
 *   - otherwise a fresh Draft PO is created for that supplier
 *
 * When the bulk order day arrives, the manager just clicks Send on the
 * accumulated draft.
 */
const AddToDraftDialog = ({ open, item, suppliers, onClose, onSaved }) => {
  const [qty, setQty] = useState(1);
  const [supplierId, setSupplierId] = useState("");
  const [pos, setPos] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    setQty(1);
    // Seed supplier from the item's default. Also load open POs so we
    // can show the "will be merged into PO-…" hint.
    const sid =
      (suppliers || []).find(
        (s) => s.name === item.supplier_default,
      )?.id || item.supplier_id || "";
    setSupplierId(sid || "");
    api
      .get("/po")
      .then(({ data }) => setPos(data || []))
      .catch(() => setPos([]));
  }, [open, item?.id, suppliers]);

  const existingDraft = useMemo(() => {
    if (!supplierId) return null;
    return pos.find(
      (p) => p.status === "draft" && !p.companion_of && p.supplier_id === supplierId,
    );
  }, [pos, supplierId]);

  const alreadyOnDraft = useMemo(() => {
    if (!existingDraft || !item) return null;
    return (existingDraft.items || []).find((it) => it.stock_id === item.id) || null;
  }, [existingDraft, item]);

  if (!open || !item) return null;

  const submit = async () => {
    const q = Number(qty);
    if (!q || q <= 0) {
      toast.error("Qty must be greater than 0");
      return;
    }
    if (!supplierId) {
      toast.error("Pick a supplier first — set a default on the item to skip this step");
      return;
    }
    setBusy(true);
    try {
      if (existingDraft) {
        await api.post(`/po/${existingDraft.id}/lines`, {
          items: [{ stock_id: item.id, qty: q, unit_cost: item.unit_cost }],
        });
        toast.success(
          alreadyOnDraft
            ? `Bumped ${item.name} → ${fmtQty(Number(alreadyOnDraft.qty) + q)} on ${existingDraft.po_number}`
            : `Added ${item.name} × ${fmtQty(q)} to ${existingDraft.po_number}`,
          { duration: 5000 },
        );
      } else {
        const { data } = await api.post("/po", {
          supplier_id: supplierId,
          items: [{ stock_id: item.id, qty: q, unit_cost: item.unit_cost }],
        });
        toast.success(`Draft ${data.po_number} created with ${item.name}`);
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="add-to-draft-overlay"
    >
      <div
        className="nb-card bg-white max-w-md w-full my-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="add-to-draft-dialog"
      >
        <div className="flex items-start justify-between p-5 border-b-2 border-black gap-3">
          <div>
            <div className="label-eyebrow">Add to draft order</div>
            <h2 className="font-display text-2xl leading-tight mt-1">
              {item.name}
            </h2>
            <div className="text-xs text-[#607087] mt-0.5">
              On shelf: <span className="font-mono-num font-bold">{fmtQty(item.current_qty)}</span>{" "}
              {item.unit}
              {Number(item.low_stock_threshold) > 0 && (
                <span className="ml-2">
                  · threshold {fmtQty(item.low_stock_threshold)}
                </span>
              )}
            </div>
          </div>
          <button
            data-testid="add-to-draft-close"
            onClick={onClose}
            className="nb-btn bg-white p-2"
            aria-label="close"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-3">
          <div>
            <div className="label-eyebrow mb-1">Supplier</div>
            <select
              data-testid="add-to-draft-supplier"
              value={supplierId}
              onChange={(e) => setSupplierId(e.target.value)}
              className="nb-input py-2 text-sm w-full"
            >
              <option value="">— pick supplier —</option>
              {(suppliers || [])
                .filter((s) => s.is_active !== false)
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <div className="label-eyebrow mb-1">Qty to add</div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="add-to-draft-minus"
                onClick={() => setQty((v) => Math.max(0, Number(v) - 1))}
                className="nb-btn bg-white p-2"
                aria-label="decrease"
              >
                <Minus size={14} strokeWidth={2.5} />
              </button>
              <input
                type="number"
                data-testid="add-to-draft-qty"
                value={qty}
                min="0"
                step="any"
                onChange={(e) => setQty(e.target.value)}
                className="nb-input font-mono-num w-24 text-center py-2"
              />
              <button
                type="button"
                data-testid="add-to-draft-plus"
                onClick={() => setQty((v) => Number(v) + 1)}
                className="nb-btn bg-white p-2"
                aria-label="increase"
              >
                <Plus size={14} strokeWidth={2.5} />
              </button>
              <span className="label-eyebrow">{item.unit}</span>
            </div>
          </div>

          {existingDraft && (
            <div
              className="border-2 border-[#0A3A7A] bg-[#DFEEF7] rounded-lg p-3 text-xs"
              data-testid="add-to-draft-existing-hint"
            >
              <div className="flex items-center gap-1.5 font-bold text-[#0A3A7A] mb-0.5">
                <FileText size={12} strokeWidth={2.5} />
                Will be added to open draft{" "}
                <span className="font-mono-num">{existingDraft.po_number}</span>
              </div>
              <div className="text-[#3D4A5C]">
                {alreadyOnDraft
                  ? `This item is already on the draft at qty ${fmtQty(alreadyOnDraft.qty)} — will bump to ${fmtQty(Number(alreadyOnDraft.qty) + (Number(qty) || 0))}.`
                  : `${(existingDraft.items || []).length} line${(existingDraft.items || []).length === 1 ? "" : "s"} on the draft so far · click Send when your bulk order day arrives.`}
              </div>
            </div>
          )}
          {!existingDraft && supplierId && (
            <div
              className="border-2 border-[#7A5A00] bg-[#FFF6DF] rounded-lg p-3 text-xs text-[#3B2A00]"
              data-testid="add-to-draft-new-hint"
            >
              No open draft for this supplier — a fresh Draft PO will be created.
              You can keep adding to it over the days ahead and Send when ready.
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              data-testid="add-to-draft-cancel"
              onClick={onClose}
              disabled={busy}
              className="nb-btn bg-white px-4 py-2 text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="add-to-draft-confirm"
              onClick={submit}
              disabled={busy || !supplierId || !(Number(qty) > 0)}
              className="nb-btn nb-btn-primary px-5 py-2 text-sm font-bold flex items-center gap-2"
            >
              <ShoppingCart size={14} strokeWidth={2.5} />
              {existingDraft ? "Add to draft" : "Create draft"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddToDraftDialog;
